// Client-side provably-fair verifier: mirrors backend/engine/shuffle.py exactly.
const enc = new TextEncoder();
const RANKS = "23456789TJQKA";
const SUITS = "cdhs";
const cardStr = (c) => RANKS[Math.floor(c / 4)] + SUITS[c % 4];
const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

export async function sha256Hex(str) {
  return hex(await crypto.subtle.digest("SHA-256", enc.encode(str)));
}

// HMAC-SHA256(seed, material:counter) read as big-endian u32s; below(n) uses rejection sampling (no modulo bias)
const hmacStream = async (seedHex, material) => {
  const key = await crypto.subtle.importKey("raw", enc.encode(seedHex), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  let counter = 0;
  let buf = new Uint8Array(0);
  const nextU32 = async () => {
    if (buf.length < 4) {
      const sig = new Uint8Array(await crypto.subtle.sign("HMAC", key, enc.encode(`${material}:${counter}`)));
      counter += 1;
      const nb = new Uint8Array(buf.length + sig.length);
      nb.set(buf);
      nb.set(sig, buf.length);
      buf = nb;
    }
    const v = buf[0] * 16777216 + (buf[1] << 16) + (buf[2] << 8) + buf[3];
    buf = buf.slice(4);
    return v >>> 0;
  };
  return async (n) => {
    const limit = Math.floor(2 ** 32 / n) * n;
    for (;;) {
      const v = await nextU32();
      if (v < limit) return v % n;
    }
  };
};

// draw-v2: no deck exists up front — card k is an unbiased pick from the 52−k cards not yet drawn (mirrors engine.shuffle.DeckStream)
export async function drawSequence(seedHex, material) {
  const below = await hmacStream(seedHex, material);
  const remaining = [...Array(52).keys()];
  const out = [];
  while (remaining.length) out.push(remaining.splice(await below(remaining.length), 1)[0]);
  return out.map(cardStr);
}

// v1 (records without shuffle.algo): Fisher-Yates over a ready deck, same stream
export async function shuffledDeck(seedHex, handId) {
  const key = await crypto.subtle.importKey("raw", enc.encode(seedHex), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  let counter = 0;
  let buf = new Uint8Array(0);
  const nextU32 = async () => {
    if (buf.length < 4) {
      const sig = new Uint8Array(await crypto.subtle.sign("HMAC", key, enc.encode(`${handId}:${counter}`)));
      counter += 1;
      const nb = new Uint8Array(buf.length + sig.length);
      nb.set(buf);
      nb.set(sig, buf.length);
      buf = nb;
    }
    const v = buf[0] * 16777216 + (buf[1] << 16) + (buf[2] << 8) + buf[3];
    buf = buf.slice(4);
    return v >>> 0;
  };
  const below = async (n) => {
    const limit = Math.floor(2 ** 32 / n) * n;
    for (;;) {
      const v = await nextU32();
      if (v < limit) return v % n;
    }
  };
  const deck = [...Array(52).keys()];
  for (let i = 51; i > 0; i--) {
    const j = await below(i + 1);
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck.map(cardStr);
}

export const nameNumber = (name) => [...name.toLowerCase()].filter((c) => c >= "a" && c <= "z").map((c) => c.charCodeAt(0) - 96).join("") || "0";

// mirrors backend shuffle_material(): hand_id | previous hand finish time | seat:name-number,… | seat:nonce,…
export const shuffleMaterial = (handId, prevEnd, names, nonces) => {
  const namePart = names.map((n) => `${n.seat}:${nameNumber(n.name)}`).join(",");
  const noncePart = Object.keys(nonces).map(Number).sort((a, b) => a - b).map((s) => `${s}:${nonces[s]}`).join(",");
  return `${handId}|${prevEnd}|${namePart}|${noncePart}`;
};

export async function verifyHand(result) {
  const commitmentOk = (await sha256Hex(result.seed)) === result.commitment;
  const sh = result.shuffle;
  const material = sh ? shuffleMaterial(result.hand_id, sh.prev_end, sh.names, sh.nonces) : result.hand_id;
  const materialOk = !sh || material === sh.material;
  const algo = sh?.algo || "v1";
  const deck = algo === "draw-v2" ? await drawSequence(result.seed, material) : await shuffledDeck(result.seed, material);
  const deckOk = JSON.stringify(deck) === JSON.stringify(result.deck);
  return { commitmentOk, materialOk, deckOk, deck, material, algo };
}

export const newNonce = () => [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");
